import { useNavigate } from 'react-router-dom'

import { useToggle } from '@factorialco/deprecated-design-system'
import { F0Card } from '@factorialco/f0-react'
import { Calendar, Feed, Star } from '@factorialco/f0-react/icons/app'
import {
  PerformanceProcessTargetEvaluationCardFragment,
  PerformanceReviewEvaluationStatusEnum,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { getEvaluationType } from 'modules/performance_process/components/review/answer/helpers/getEvaluationType'
import { ReviewerSelectorModal } from 'modules/performance_process/components/targetEvaluations/ReviewerSelectorModal'
import { statusColor, tagLabel } from 'modules/performance_process/lib/answerTag'

const getDateLabel = ({
  status,
  publishedAt,
  republishedAt,
  endsAt,
}: {
  status: PerformanceReviewEvaluationStatusEnum
  publishedAt?: string | null
  republishedAt?: string | null
  endsAt?: string | null
}): string => {
  const noDataLabel = i18n.t('performance_process.insights.no_data')

  if (status === 'published') {
    if (republishedAt) {
      return i18n.t('performance_process.reviews.answer_evaluation.actions.republished_on', {
        date: i18nDateToString(new Date(republishedAt), 'ui'),
      })
    }

    if (publishedAt) {
      return i18n.t('performance_process.reviews.answer_evaluation.actions.published_on', {
        date: i18nDateToString(new Date(publishedAt), 'ui'),
      })
    }

    return `${i18n.t(
      'performance_process.reviews.answer_evaluation.answer_status.published'
    )}: ${noDataLabel}`
  }

  const dueDateLabel = i18n.t('performance_process.reviews.common.due_date')
  return endsAt
    ? `${dueDateLabel} ${i18nDateToString(new Date(endsAt), 'ui')}`
    : `${dueDateLabel}: ${noDataLabel}`
}

export const EvaluationReviewerCard: React.FC<{
  evaluation: PerformanceProcessTargetEvaluationCardFragment
  reviewId: number
  targetId: string | undefined
  strategyReviewerAccessIds: number[]
  isCheckingOwnResults?: boolean
  showEvaluationScore?: boolean
  isRepublishFeatureEnabled?: boolean
}> = ({
  evaluation: {
    id,
    evaluationType,
    targetAccess,
    reviewerAccess,
    status,
    _policies,
    answeredQuestionnaires,
    answeredQuestionnairesIdentifier,
    evaluationScore,
    publishedAt,
    republishedAt,
    performanceReviewProcess,
  },
  reviewId,
  targetId,
  strategyReviewerAccessIds,
  isCheckingOwnResults = false,
  showEvaluationScore = false,
  isRepublishFeatureEnabled = false,
}) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [showModal, toggleShowModal] = useToggle()

  const { title: evaluationTypeTitle } = evaluationType
    ? getEvaluationType(evaluationType)
    : { title: '' }
  const canReplaceReviewer = _policies?.replaceReviewer.can && targetAccess && reviewerAccess
  const visibleAnwsers = !!answeredQuestionnairesIdentifier?.id
  const link = isCheckingOwnResults
    ? navigation.performanceProcess.reviews.review.myResultsEvaluation.fullPath({
        reviewId: reviewId,
        targetId: targetId ?? '',
        evaluationId: id,
      })
    : navigation.performanceProcess.reviews.review.target.evaluation.fullPath({
        reviewId: reviewId,
        targetId: targetId ?? '',
        evaluationId: id,
      })

  const handleCardClick = () => !!visibleAnwsers && navigate(link)

  const canRepublish =
    isRepublishFeatureEnabled && (answeredQuestionnaires?._policies?.republish?.can ?? false)

  const handleRepublish = () => {
    if (canRepublish) {
      navigate(link, { state: { isRepublishing: true } })
    }
  }

  const excludedAccessIds: Set<number> = new Set(
    [reviewerAccess?.id, ...strategyReviewerAccessIds].filter(Boolean)
  )

  const evaluationScoreValue = evaluationScore?.score

  // TODO: anonymous peer review check
  const reviewerName = reviewerAccess
    ? `${reviewerAccess.fullName}${
        reviewerAccess.current ? ' (' + i18n.t('performance_process.reviews.target_you') + ')' : ''
      }`
    : i18n.t('performance_process.reviews.evaluations.anonymous')

  // TODO: anonymous peer review check
  const avatar = {
    type: 'person' as const,
    firstName: reviewerAccess?.fullName || '',
    lastName: '',
    src: reviewerAccess?.avatar ? buildUrl(reviewerAccess.avatar) : undefined,
  }

  const cardMetadata = [
    {
      icon: Feed,
      property: {
        type: 'status' as const,
        value: {
          label: tagLabel(status),
          status: statusColor(status),
        },
      },
    },
    {
      icon: Calendar,
      property: {
        type: 'text' as const,
        value: getDateLabel({
          status,
          publishedAt,
          republishedAt,
          endsAt: performanceReviewProcess?.endsAt,
        }),
      },
    },
    ...(showEvaluationScore && evaluationScoreValue != null
      ? [
          {
            icon: Star,
            property: {
              type: 'text' as const,
              value: `${i18n.t(
                'performance_process.reviews.target_datatable.final_score.title'
              )}: ${i18n.formatNumber(evaluationScoreValue)}`,
            },
          },
        ]
      : []),
  ]

  const actions = [
    canRepublish && {
      label: i18n.t('performance_process.reviews.evaluations.edit'),
      onClick: handleRepublish,
    },
    canReplaceReviewer && {
      label: i18n.t('performance_process.reviews.evaluations.replace_reviewer'),
      onClick: toggleShowModal(true),
      critical: true,
    },
  ].filter(Boolean)

  const cardActions = actions.length > 0 ? actions : undefined

  return (
    <>
      <F0Card
        avatar={avatar}
        title={reviewerName}
        description={evaluationTypeTitle}
        onClick={handleCardClick}
        aria-label={reviewerName}
        fullHeight
        metadata={cardMetadata}
        otherActions={cardActions}
      />
      {showModal && canReplaceReviewer && (
        <ReviewerSelectorModal
          onClose={toggleShowModal(false)}
          evaluationId={id}
          excludedAccessIds={excludedAccessIds}
        />
      )}
    </>
  )
}
